import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { useCellStatuses } from '@features/executionDashboard/hooks/useCellStatuses';
import {
  DatasheetPane,
  InstrumentRail,
  RehomeConfirmDialog,
  RerunConfirmDialog,
  RunHeader
} from './components';
import {
  useActiveWorkOrderRun,
  useAssignCellScript,
  useFollowRobot,
  useLaunchRun,
  useRunInstruments
} from './hooks';
import type { RunInstrument } from './hooks';

const RAIL_WIDTH = 340;

const CalibrationRunPage: React.FC = () => {
  const { jobNumber } = useParams<{ jobNumber: string }>();
  const { t } = useTranslation('calibration');
  const [searchParams, setSearchParams] = useSearchParams();

  const { instruments, rollup, isLoading, error } = useRunInstruments(
    jobNumber ?? ''
  );
  const activeRun = useActiveWorkOrderRun(jobNumber ?? '');
  const { cells: liveCells } = useCellStatuses(activeRun.activeRunId);

  const activeCell = useMemo(
    () => liveCells.find(cell => cell.status === 'running')?.cell ?? null,
    [liveCells]
  );

  const requestedInstrumentId = searchParams.get('instrument');

  const selectedInstrumentId = useMemo(() => {
    if (
      requestedInstrumentId &&
      instruments.some(i => i.woDetailGlobalId === requestedInstrumentId)
    ) {
      return requestedInstrumentId;
    }
    return instruments[0]?.woDetailGlobalId ?? null;
  }, [requestedInstrumentId, instruments]);

  const selectedInstrument = useMemo(
    () =>
      instruments.find(i => i.woDetailGlobalId === selectedInstrumentId) ??
      null,
    [instruments, selectedInstrumentId]
  );

  const updateInstrumentParam = useCallback(
    (woDetailGlobalId: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('instrument', woDetailGlobalId);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { following, activeInstrumentId, pauseFollowing, resumeFollowing } =
    useFollowRobot({
      instruments,
      activeCell,
      isRunning: activeRun.status === 'RUNNING',
      selectedInstrumentId,
      onFollowSelect: updateInstrumentParam
    });

  const handleSelectInstrument = useCallback(
    (woDetailGlobalId: string) => {
      pauseFollowing();
      updateInstrumentParam(woDetailGlobalId);
    },
    [pauseFollowing, updateInstrumentParam]
  );

  const launch = useLaunchRun(jobNumber ?? '');

  const handleStartRun = useCallback(async () => {
    const run = await launch.launchFullRun();
    if (!run) return;
    window.setTimeout(
      () => activeRun.control.sendCommand('START', run.id),
      300
    );
  }, [launch, activeRun.control]);

  const canStartRun = activeRun.control.isConnected && launch.canLaunch;

  const [dismissedFailureKey, setDismissedFailureKey] = useState<
    string | null
  >(null);

  const failureKey =
    activeRun.activeRunId &&
    (activeRun.status === 'FAILED' || activeRun.status === 'REJECTED')
      ? `${activeRun.activeRunId}:${activeRun.status}`
      : null;

  const runFailure = useMemo(
    () =>
      failureKey && failureKey !== dismissedFailureKey
        ? {
            status: activeRun.status as 'FAILED' | 'REJECTED',
            reason: activeRun.runStatus?.errorMessage ?? null
          }
        : null,
    [failureKey, dismissedFailureKey, activeRun.status, activeRun.runStatus]
  );

  const handleDismissRunFailure = useCallback(
    () => setDismissedFailureKey(failureKey),
    [failureKey]
  );

  const startRunBlockedReason = !activeRun.control.isConnected
    ? 'disconnected'
    : launch.blockedReason;

  const assignCellScript = useAssignCellScript(jobNumber ?? '');

  const handleAssignScript = useCallback(
    async (scriptGlobalId: string) => {
      if (!selectedInstrument?.cell || !selectedInstrument.equipmentId) return;
      await assignCellScript.assignScript(
        selectedInstrument.cell,
        selectedInstrument.equipmentId,
        scriptGlobalId
      );
    },
    [assignCellScript, selectedInstrument]
  );

  const [rerunTarget, setRerunTarget] = useState<RunInstrument | null>(null);

  const [rehomeConfirmOpen, setRehomeConfirmOpen] = useState(false);

  const handleRequestRehome = useCallback(
    () => setRehomeConfirmOpen(true),
    []
  );
  const handleCancelRehome = useCallback(
    () => setRehomeConfirmOpen(false),
    []
  );
  const handleConfirmRehome = useCallback(() => {
    setRehomeConfirmOpen(false);
    activeRun.control.publishRehome();
  }, [activeRun.control]);

  const isCellAssigned = useCallback(
    (cell: string | null) => !!cell && launch.cellIsAssigned(cell),
    [launch]
  );

  const isSelectedCellAssigned = isCellAssigned(
    selectedInstrument?.cell ?? null
  );

  const selectedCellScriptGlobalId = useMemo(() => {
    const cell = selectedInstrument?.cell;
    if (!cell) return null;
    return launch.allCells.find(entry => entry.cell === cell)?.scriptGlobalId ??
      null;
  }, [selectedInstrument, launch.allCells]);

  const selectedCellGroups = selectedInstrument?.cell
    ? launch.getCellSelection(selectedInstrument.cell)?.groups
    : undefined;

  const handleSelectedGroupsChange = useCallback(
    (groups: string[] | undefined) => {
      if (selectedInstrument?.cell) {
        launch.setCellGroups(selectedInstrument.cell, groups);
      }
    },
    [launch, selectedInstrument]
  );

  const selectedCellPoints = selectedInstrument?.cell
    ? launch.getCellSelection(selectedInstrument.cell)?.points
    : undefined;

  const handleSelectedPointsChange = useCallback(
    (points: string[] | undefined) => {
      if (selectedInstrument?.cell) {
        launch.setCellPoints(selectedInstrument.cell, points);
      }
    },
    [launch, selectedInstrument]
  );

  const handleRequestRerun = useCallback(
    (instrument: RunInstrument) => setRerunTarget(instrument),
    []
  );

  const handleCancelRerun = useCallback(() => setRerunTarget(null), []);

  const handleConfirmRerun = useCallback(async () => {
    const cell = rerunTarget?.cell ?? null;
    setRerunTarget(null);
    if (!cell) return;
    const run = await launch.launchCell(cell);
    if (!run) return;
    window.setTimeout(
      () => activeRun.control.sendCommand('START', run.id),
      300
    );
  }, [rerunTarget, launch, activeRun.control]);

  const activeProgress = useMemo(() => {
    const total = activeRun.itemStatus?.totalSteps ?? 0;
    const current = activeRun.itemStatus?.currentStep ?? 0;
    return total > 0 ? Math.min(100, (current / total) * 100) : null;
  }, [activeRun.itemStatus]);

  const datasheetLive = useMemo(
    () =>
      activeRun.isRunActive &&
      activeRun.activeRunId &&
      selectedInstrument?.woDetailGlobalId === activeInstrumentId
        ? { runId: activeRun.activeRunId }
        : null,
    [
      activeRun.isRunActive,
      activeRun.activeRunId,
      selectedInstrument,
      activeInstrumentId
    ]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Paper
        square
        variant="outlined"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t('runPage.title', { defaultValue: 'Calibration Run' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {jobNumber}
        </Typography>
        <RunHeader
          isRunActive={activeRun.isRunActive}
          status={activeRun.status}
          isDryRun={activeRun.isDryRun}
          isConnected={activeRun.control.isConnected}
          onStart={activeRun.control.publishStart}
          onPause={activeRun.control.publishPause}
          onResume={activeRun.control.publishResume}
          onAbort={activeRun.control.publishAbort}
          following={activeRun.isRunActive ? following : undefined}
          onPauseFollowing={pauseFollowing}
          onResumeFollowing={resumeFollowing}
          onStartRun={handleStartRun}
          onRehome={handleRequestRehome}
          startMode={launch.startMode}
          onStartModeChange={launch.setStartMode}
          canStartRun={canStartRun}
          startRunBlockedReason={startRunBlockedReason}
          isLaunching={launch.isLaunching}
          launchError={launch.error}
          onDismissLaunchError={launch.clearError}
          runFailure={runFailure}
          onDismissRunFailure={handleDismissRunFailure}
        />
      </Paper>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minHeight: 0
        }}
      >
        <Box
          sx={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            borderRight: theme => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <InstrumentRail
            instruments={instruments}
            rollup={rollup}
            selectedInstrumentId={selectedInstrumentId}
            onSelect={handleSelectInstrument}
            isLoading={isLoading}
            liveCells={liveCells}
            activeInstrumentId={activeInstrumentId}
            activeProgress={activeProgress}
            isRunLive={activeRun.isRunActive}
            showRerun={!activeRun.isRunActive}
            isCellAssigned={isCellAssigned}
            onRerunInstrument={handleRequestRerun}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {error ? (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          ) : (
            <DatasheetPane
              instrument={selectedInstrument}
              live={datasheetLive}
              events={activeRun.events}
              isRunActive={activeRun.isRunActive}
              isCellAssigned={isSelectedCellAssigned}
              isAssignmentLoading={launch.isAssignmentLoading}
              startBlockedReason={startRunBlockedReason}
              onAssignScript={handleAssignScript}
              isAssigningScript={assignCellScript.isAssigning}
              assignedScriptGlobalId={selectedCellScriptGlobalId}
              selectedGroups={selectedCellGroups}
              onSelectedGroupsChange={handleSelectedGroupsChange}
              selectedPoints={selectedCellPoints}
              onSelectedPointsChange={handleSelectedPointsChange}
            />
          )}
        </Box>
      </Box>

      <RerunConfirmDialog
        open={!!rerunTarget}
        instrument={rerunTarget}
        onConfirm={handleConfirmRerun}
        onCancel={handleCancelRerun}
      />

      <RehomeConfirmDialog
        open={rehomeConfirmOpen}
        onConfirm={handleConfirmRehome}
        onCancel={handleCancelRehome}
      />
    </Box>
  );
};

export default CalibrationRunPage;
